import type { Knock, QuestionRow, ReadRow, RoomInviteRow, ShareAskRow } from '../api';

/**
 * A letter (v4.0 §4.9): what is waiting on THIS person — Ember's ask whether
 * it may let people read an Ember of theirs, a note from someone who read
 * it, the teller's words back to a reader, a question to answer, a room
 * invitation. Letters are what the tree shows at the figure's feet, what the
 * chat names in its door line, and what the paper lists. Settled rows are
 * not letters; they live on the Ember pages.
 *
 * The server decides (edge letters.js `letterKindOf`, one rule for the badge,
 * the talk model, the text and the push): each row carries `letter`, the
 * kind waiting on this side right now, or null. The web keeps no rule.
 */
export function isLetter(k: Knock): boolean {
  return k.letter !== null;
}

/**
 * One row is several letters in its life — a reader's note, then another —
 * so each carries its own id: the envelope's memory of having opened one
 * must not apply to the next.
 */
export const letterId = (k: Knock): string => `${k.letter ?? k.role}:${k.match_id}`;

/** A question row (§4.19). */
export const isQuestion = (k: Knock): k is QuestionRow => k.role === 'asker' || k.role === 'answerer';

/** A room invitation (§4.21). */
export const isRoomInvite = (k: Knock): k is RoomInviteRow => k.role === 'member';

/** The ask whether Ember may let people read an Ember of this person's (§4.9). */
export const isShareAsk = (k: Knock): k is ShareAskRow => k.role === 'share';

/**
 * A letter someone is waiting on this person to answer (edge letters.js
 * REQUEST_KINDS): a room invitation, a question carried to them, the share
 * ask. On the front page Ember walks over to the envelopes for one of these
 * while it is still sealed (2026-09-29 拍板); a delivery — a note, a reply,
 * answers back — lies at the feet like before.
 */
export const isAwaited = (k: Knock): boolean => k.letter === 'room_invite' || k.letter === 'question_asked' || k.letter === 'share_ask';

/** What the letter is about, for logging: its kind, or the row's state once it is none. */
export const letterState = (k: Knock): string => {
  if (k.letter) return k.letter;
  if (isQuestion(k)) return k.question.status;
  if (isRoomInvite(k)) return k.answer ?? 'invited';
  if (isShareAsk(k)) return k.answer ?? 'asked';
  return k.state;
};

/** The other side of a read has written something this side has not looked at yet. */
export function unseenNotes(k: ReadRow): boolean {
  const other = k.role === 'read_owner' ? 'reader' : 'owner';
  return k.notes.some((n) => n.by === other && !n.seen);
}

/**
 * The one sentence each letter says — the card's head, the letter row's
 * head, the chat's door line. §0.8 H: Ember is asking, not a notice pushing.
 */
export function knockHeadline(k: Knock): string {
  // A room (§4.21): the invitation, then the answer just given.
  if (k.role === 'member') {
    if (k.answer === 'yes') return 'You said yes.';
    if (k.answer === 'no') return 'All right.';
    // Someone's yes already given (2026-09-28 拍板): the line says the room is waiting on them.
    const n = k.room.yeses?.length ?? 0;
    if (n === 1) return `${k.room.yeses[0].name} said yes to a room with you.`;
    if (n > 1) return `${n} people said yes to a room with you.`;
    return 'A room, if you want it.';
  }
  // Ask people (§4.19): the question letters' one sentence each.
  if (k.role === 'answerer') {
    if (k.letter === 'answer_mattered' || k.mine?.mattered) return 'It mattered to them.';
    if (k.mine) return 'You answered.';
    if (k.passed) return 'Left for others.';
    if (k.question.withdrawn_at) return 'They withdrew the question.';
    if (k.question.status === 'closed') return 'The question has closed.';
    return 'Someone is asking something you may have lived.';
  }
  if (k.role === 'asker') {
    const n = k.answers.length;
    if (k.question.withdrawn_at) return n ? 'Your question, withdrawn.' : 'Withdrawn.';
    if (k.question.status === 'closed') return n ? 'Your question, closed.' : 'Closed.';
    if (n === 0) return 'Your question is out with people.';
    return n === 1 ? 'Someone answered your question.' : `${n} people answered your question.`;
  }
  // Sharing (§4.9): the ask, then the answer just given.
  if (k.role === 'share') {
    if (k.answer === 'yes') return 'You said yes.';
    if (k.answer === 'no') return 'All right.';
    return 'Something in your Ember shines.';
  }
  if (k.role === 'read_owner') {
    return unseenNotes(k) || k.letter === 'read_note' ? 'Someone who read it wrote to you.' : 'Someone read it.';
  }
  return unseenNotes(k) || k.letter === 'read_reply' ? 'They wrote back.' : 'An Ember someone let you read.';
}

/** The Ember a letter is about, as the person named it. */
export function knockAbout(k: Knock): string {
  if (isQuestion(k) || isRoomInvite(k)) return '';
  return k.ember.title;
}

/*
 * What this browser remembers about its letters. Two facts, both local:
 *
 *   opened — the person unfolded this letter (expanded its row in the sheet).
 *            A sealed envelope becomes an opened one; the letter stays until
 *            it is answered. Per browser, kept across visits (localStorage).
 *   landed — this letter has already flown down in this visit. A letter flies
 *            in once; every later look at the tree finds it on the ground
 *            (sessionStorage — a new visit, a new arrival).
 *
 * Neither is product state: the letter's state lives on the server and is
 * untouched. These are the envelope's own memory, like the tree's tag
 * demonstration knowing it already ran.
 */
const OPENED_KEY = 'ember.letters.opened';
const LANDED_KEY = 'ember.letters.landed';

function readSet(storage: () => Storage, key: string): Set<string> {
  try {
    const raw = storage().getItem(key);
    const list: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeSet(storage: () => Storage, key: string, set: Set<string>): void {
  try {
    storage().setItem(key, JSON.stringify([...set].slice(-200)));
  } catch { /* the envelope forgets; nothing else changes */ }
}

export const letterOpened = (id: string): boolean => readSet(() => window.localStorage, OPENED_KEY).has(id);
export function markLetterOpened(id: string): void {
  const set = readSet(() => window.localStorage, OPENED_KEY);
  set.add(id);
  writeSet(() => window.localStorage, OPENED_KEY, set);
}

export const letterLanded = (id: string): boolean => readSet(() => window.sessionStorage, LANDED_KEY).has(id);
export function markLetterLanded(id: string): void {
  const set = readSet(() => window.sessionStorage, LANDED_KEY);
  set.add(id);
  writeSet(() => window.sessionStorage, LANDED_KEY, set);
}
